import React, { useState, useRef, useEffect, useCallback } from 'react';
import { auth } from '../config/firebase';
import './Terminal.css';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

interface Message {
  id: string;
  type: 'system' | 'user' | 'assistant' | 'code' | 'error' | 'tool';
  content: string;
  code?: string;
  css?: string;
  appName?: string;
  appEmoji?: string;
  toolName?: string;
  toolStatus?: 'running' | 'success' | 'error';
}

interface UserApp {
  id: string;
  appId: string;
  name: string;
  emoji: string;
  code: string;
  css: string;
  createdAt: number;
  updatedAt: number;
  isPublished: boolean;
  description: string;
  format?: string;   // 'jsx-v1' (default) | 'html-v2' (built externally via MCP)
}

interface PublishedApp {
  id: string;
  appId: string;
  name: string;
  emoji: string;
  code: string;
  css: string;
  authorId: string;
  authorHandle: string;
  publishedAt: number;
  description: string;
}

interface TerminalProps {
  onSaveApp: (app: Omit<UserApp, 'id' | 'appId' | 'createdAt' | 'updatedAt' | 'isPublished'>) => Promise<string>;
  onUpdateApp: (appId: string, app: Partial<UserApp>) => Promise<void>;
  onDeleteApp: (appId: string) => Promise<void>;
  onPublishApp: (appId: string) => Promise<void>;
  onUnpublishApp: (appId: string) => Promise<void>;
  onInstallApp: (publishedAppId: string) => Promise<void>;
  onOpenPreview: (code: string, css: string, appName: string, appEmoji: string) => void;
  onOpenCodeEditor: (code: string, css: string, appName: string, onSave: (code: string, css: string) => void) => void;
  userApps: UserApp[];
  publishedApps: PublishedApp[];
  installedAppIds: string[];
  isLoggedIn: boolean;
  userHandle: string | null;
  onOpenLoginSignup: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const API_URL = process.env.REACT_APP_API_URL || 'https://us-central1-moodboards-b8265.cloudfunctions.net/app';
const MAX_TOOL_CALLS = 12;
const MODEL = 'claude-haiku-4-5';
const MAX_TOKENS = 8192;

const EMOJI_CHOICES = [
  '📱', '🎮', '🎵', '🎨', '📝', '📋', '🕰️', '⏱️', '📅', '🧮', '💬', '🌦️',
  '🌍', '🚀', '🛸', '🌙', '⭐', '☄️', '🔮', '🎲', '🃏', '🎯', '🏆', '💾',
  '🖥️', '⌨️', '🖱️', '📟', '💿', '🧲', '🔋', '💡', '🔍', '✏️', '📌', '✂️',
  '🌵', '🌸', '🍄', '🐸', '🐱', '🦴', '🍕', '☕', '🧊', '💊', '🫧', '👾'
];

// System prompt for HOME mode (creating new apps)
const HOME_SYSTEM_PROMPT = `You are the app builder inside the orbit terminal on inorbit.fm — a retro Mac OS that runs in the browser. You help users create small React apps by chatting.

Default to building immediately with reasonable assumptions. Only ask a question if the request is truly ambiguous.

When you build (or rebuild) an app:
1. Generate ONE React functional component named "App".
2. It receives an "orbit" prop with these APIs:
   - orbit.music: play(), pause(), toggle(), next(), prev(), playTrack(index)
   - orbit.user: { handle, isLoggedIn }
   - orbit.notify(message): show an OS notification
   - orbit.storage: get(key), set(key, value), clear() — persistent per-app storage
3. Use hooks directly (useState, useEffect, useRef...). Do NOT include import or export statements — React and hooks are pre-loaded globally.
4. Put the complete JSX in a \`\`\`jsx fenced block and the complete CSS in a \`\`\`css fenced block.
5. A retro Mac / pixel aesthetic fits the OS, but follow the user's style requests.

If the user asks for changes before saving, output the FULL updated \`\`\`jsx and \`\`\`css blocks again — they replace the previous version.

Always end with exactly these two lines:
App name: <short name>
App icon: <one emoji>`;

// System prompt for PROJECT mode (editing existing apps)
const PROJECT_SYSTEM_PROMPT = `You are the coding assistant for an existing React app in the orbit terminal on inorbit.fm (a retro Mac OS in the browser).

Tools available:
- get_code: read the current JSX and CSS
- str_replace: make a targeted edit by replacing an exact string (PREFERRED for small/medium changes)
- edit_code: replace the whole JSX (and optionally CSS) — use only for rewrites or many scattered changes
- get_app_info: read app metadata (name, emoji, id, description, published status)
- update_app_info: change the app's name, emoji, or description
- open_preview: open/refresh the live preview window
- publish_app: save + publish to the public store
- unpublish_app: remove from the store

Rules for the code:
- The component must stay a function named "App" that receives the "orbit" prop (music, user, notify, storage APIs).
- NO import or export statements. React and hooks are pre-loaded globally.
- After any code change the preview refreshes and changes are auto-saved — no extra action needed.

Workflow: call get_code first, make the change (str_replace for small edits, edit_code for big ones), then reply with a one-or-two sentence summary. Keep responses short.`;

// Tool definitions for Claude API
const TOOLS = [
  {
    name: 'get_code',
    description: 'Get the current app code (JSX) and CSS',
    input_schema: {
      type: 'object' as const,
      properties: {},
      required: []
    }
  },
  {
    name: 'str_replace',
    description: 'Make a targeted edit: replace one exact occurrence of old_str with new_str in the code or CSS. old_str must match exactly (including whitespace) and be unique — include surrounding lines for context if needed. Preferred over edit_code for small changes.',
    input_schema: {
      type: 'object' as const,
      properties: {
        target: {
          type: 'string',
          enum: ['code', 'css'],
          description: 'Which text to edit: "code" (JSX) or "css"'
        },
        old_str: {
          type: 'string',
          description: 'Exact existing text to replace (must be unique in the target)'
        },
        new_str: {
          type: 'string',
          description: 'Replacement text (can be empty to delete)'
        }
      },
      required: ['target', 'old_str', 'new_str']
    }
  },
  {
    name: 'edit_code',
    description: 'Replace the entire app code. Use for full rewrites or many scattered changes; prefer str_replace for small edits.',
    input_schema: {
      type: 'object' as const,
      properties: {
        code: {
          type: 'string',
          description: 'The new complete JSX code for the app'
        },
        css: {
          type: 'string',
          description: 'The new complete CSS. Omit to keep the current CSS unchanged.'
        }
      },
      required: ['code']
    }
  },
  {
    name: 'get_app_info',
    description: 'Get app metadata: name, emoji, ID, description, published status',
    input_schema: {
      type: 'object' as const,
      properties: {},
      required: []
    }
  },
  {
    name: 'update_app_info',
    description: "Update the app's name, emoji icon, and/or description. The app's URL id stays the same.",
    input_schema: {
      type: 'object' as const,
      properties: {
        name: { type: 'string', description: 'New display name' },
        emoji: { type: 'string', description: 'New emoji icon (single emoji)' },
        description: { type: 'string', description: 'New short description' }
      },
      required: []
    }
  },
  {
    name: 'open_preview',
    description: 'Open or refresh the live preview window',
    input_schema: {
      type: 'object' as const,
      properties: {},
      required: []
    }
  },
  {
    name: 'publish_app',
    description: 'Publish or update the app in the public store. Saves current changes and publishes.',
    input_schema: {
      type: 'object' as const,
      properties: {},
      required: []
    }
  },
  {
    name: 'unpublish_app',
    description: 'Remove the app from the public store',
    input_schema: {
      type: 'object' as const,
      properties: {},
      required: []
    }
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// PARSING HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

// Tolerant parser for generated apps: accepts jsx/tsx/js/javascript fences (or any
// non-css fence as a fallback), and strips markdown around name/icon lines.
const parseGeneratedApp = (text: string) => {
  const cssMatch = text.match(/```css\s*\n([\s\S]*?)```/i);

  let code: string | null = null;
  const codeMatch = text.match(/```(?:jsx|tsx|javascript|js)\s*\n([\s\S]*?)```/i);
  if (codeMatch) {
    code = codeMatch[1].trim();
  } else {
    const allFences = Array.from(text.matchAll(/```(\w*)\s*\n([\s\S]*?)```/g));
    const candidate = allFences.find(m => m[1].toLowerCase() !== 'css');
    if (candidate) code = candidate[2].trim();
  }

  const nameMatch = text.match(/app name:\s*[*_"'`[\]]*([^\n*_"'`[\]]+)/i);
  const emojiMatch = text.match(/app icon:\s*[*_"'`[\]]*(\S+)/i);

  const explanation = text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/app (name|icon):.*$/gim, '')
    .trim();

  return {
    code,
    css: cssMatch ? cssMatch[1].trim() : '',
    name: nameMatch ? nameMatch[1].trim().slice(0, 40) : 'My App',
    emoji: emojiMatch ? emojiMatch[1].trim() : '📱',
    explanation
  };
};

// Trim conversation history from the front without splitting tool_use/tool_result
// pairs: only cut at plain user text messages.
const trimHistory = (history: Array<{ role: string; content: any }>, max: number) => {
  if (history.length <= max) return history;
  let start = history.length - max;
  while (start < history.length && !(history[start].role === 'user' && typeof history[start].content === 'string')) {
    start++;
  }
  return start >= history.length ? history : history.slice(start);
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const Terminal: React.FC<TerminalProps> = ({
  onSaveApp,
  onUpdateApp,
  onDeleteApp,
  onPublishApp,
  onUnpublishApp,
  onInstallApp,
  onOpenPreview,
  onOpenCodeEditor,
  userApps,
  publishedApps,
  installedAppIds,
  isLoggedIn,
  userHandle,
  onOpenLoginSignup
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────

  const [mode, setMode] = useState<'home' | 'project'>('home');
  const [activeProject, setActiveProject] = useState<UserApp | null>(null);
  const [projectCode, setProjectCode] = useState<string>('');
  const [projectCss, setProjectCss] = useState<string>('');

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{role: string; content: any}>>([]);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Info editor (emoji / name / description)
  const [infoEditor, setInfoEditor] = useState<{
    app: UserApp;
    name: string;
    emoji: string;
    description: string;
  } | null>(null);
  const [infoSaving, setInfoSaving] = useState(false);

  // For home mode app creation
  const [pendingApp, setPendingApp] = useState<{
    code: string;
    css: string;
    name: string;
    emoji: string;
  } | null>(null);

  // Refs mirror project state so sequential tool calls in one agentic turn see
  // fresh values (React state is stale inside the same closure).
  const projectCodeRef = useRef('');
  const projectCssRef = useRef('');
  const activeProjectRef = useRef<UserApp | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const hasShownWelcomeRef = useRef(false);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  // Initialize home mode welcome (only once per session)
  useEffect(() => {
    if (mode === 'home' && messages.length === 0 && !hasShownWelcomeRef.current) {
      hasShownWelcomeRef.current = true;
      setMessages([{
        id: `msg-welcome-${Date.now()}`,
        type: 'system',
        content: isLoggedIn
          ? `Welcome back, @${userHandle}!\nDescribe an app to create it, or click one of your apps to edit.`
          : 'Welcome to Orbit Terminal\nDescribe an app and I\'ll build it. Login to create and save apps.'
      }]);
    }
  }, [mode, isLoggedIn, userHandle, messages.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // MESSAGE HELPERS
  // ─────────────────────────────────────────────────────────────────────────────

  const addMessage = (message: Omit<Message, 'id'>) => {
    const id = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setMessages(prev => [...prev, { ...message, id }]);
  };

  const updateLastMessage = (updates: Partial<Message>) => {
    setMessages(prev => {
      if (prev.length === 0) return prev;
      const newMessages = [...prev];
      newMessages[newMessages.length - 1] = { ...newMessages[newMessages.length - 1], ...updates };
      return newMessages;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // PROJECT STATE HELPERS (refs + state kept in sync)
  // ─────────────────────────────────────────────────────────────────────────────

  const setActiveProjectBoth = (app: UserApp | null) => {
    activeProjectRef.current = app;
    setActiveProject(app);
  };

  const persistProject = async (updates: Partial<UserApp>) => {
    const app = activeProjectRef.current;
    if (!app) return;
    setSaveStatus('saving');
    try {
      await onUpdateApp(app.id, updates);
      setSaveStatus('saved');
    } catch (error) {
      console.error('Autosave failed:', error);
      setSaveStatus('error');
    }
  };

  // Apply a code/css change: update state+refs, refresh preview, autosave.
  const applyProjectCode = (code: string, css: string) => {
    projectCodeRef.current = code;
    projectCssRef.current = css;
    setProjectCode(code);
    setProjectCss(css);

    const app = activeProjectRef.current;
    if (app) {
      onOpenPreview(code, css, app.name, app.emoji);
      persistProject({ code, css });
      if (app.isPublished) setHasUnpublishedChanges(true);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // PROJECT MODE
  // ─────────────────────────────────────────────────────────────────────────────

  const enterProject = (app: UserApp) => {
    if (app.format === 'html-v2') {
      addMessage({
        type: 'system',
        content: `${app.emoji} ${app.name} was built externally (Claude Code / MCP), so it can't be edited in this chat.\nEdit it where you built it and republish — or use ✎ to rename, /publish, /unpublish, /delete to manage it here.`
      });
      return;
    }
    setActiveProjectBoth(app);
    projectCodeRef.current = app.code;
    projectCssRef.current = app.css;
    setProjectCode(app.code);
    setProjectCss(app.css);
    setMode('project');
    setMessages([]);
    setConversationHistory([]);
    setSaveStatus('saved');
    setHasUnpublishedChanges(false);

    addMessage({
      type: 'system',
      content: `Editing ${app.emoji} ${app.name}\nDescribe a change in plain words — edits save automatically. /help for commands.`
    });

    // Auto-open preview
    onOpenPreview(app.code, app.css, app.name, app.emoji);
  };

  const exitProject = async () => {
    if (activeProjectRef.current) {
      // Safety save (edits are normally already autosaved)
      try {
        await onUpdateApp(activeProjectRef.current.id, {
          code: projectCodeRef.current,
          css: projectCssRef.current
        });
      } catch (error) {
        console.error('Failed to save:', error);
      }
    }

    setMode('home');
    setActiveProjectBoth(null);
    projectCodeRef.current = '';
    projectCssRef.current = '';
    setProjectCode('');
    setProjectCss('');
    setMessages([]);
    setConversationHistory([]);
    setPendingApp(null);
    setSaveStatus('idle');
    setHasUnpublishedChanges(false);
    hasShownWelcomeRef.current = false; // Reset so welcome shows again
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // TOOL HANDLERS (for project mode)
  // ─────────────────────────────────────────────────────────────────────────────

  const executeToolCall = async (toolName: string, toolInput: any): Promise<string> => {
    const project = activeProjectRef.current;
    if (!project) return 'Error: No active project';

    switch (toolName) {
      case 'get_code':
        return JSON.stringify({
          code: projectCodeRef.current,
          css: projectCssRef.current
        }, null, 2);

      case 'str_replace': {
        const { target, old_str, new_str } = toolInput;
        if (target !== 'code' && target !== 'css') {
          throw new Error('target must be "code" or "css"');
        }
        const source = target === 'css' ? projectCssRef.current : projectCodeRef.current;
        const occurrences = source.split(old_str).length - 1;
        if (occurrences === 0) {
          throw new Error(`old_str not found in ${target}. Use get_code to see the current text.`);
        }
        if (occurrences > 1) {
          throw new Error(`old_str matches ${occurrences} places in ${target}. Include more surrounding context to make it unique.`);
        }
        const updated = source.replace(old_str, new_str);
        if (target === 'css') {
          applyProjectCode(projectCodeRef.current, updated);
        } else {
          applyProjectCode(updated, projectCssRef.current);
        }
        return `Replaced in ${target}. Preview refreshed, changes saved.`;
      }

      case 'edit_code': {
        const { code, css } = toolInput;
        applyProjectCode(code, css !== undefined ? css : projectCssRef.current);
        return 'Code updated. Preview refreshed, changes saved.';
      }

      case 'get_app_info':
        return JSON.stringify({
          name: project.name,
          emoji: project.emoji,
          appId: project.appId,
          description: project.description,
          isPublished: project.isPublished
        }, null, 2);

      case 'update_app_info': {
        const updates: Partial<UserApp> = {};
        if (toolInput.name) updates.name = String(toolInput.name).slice(0, 40);
        if (toolInput.emoji) updates.emoji = String(toolInput.emoji).trim().slice(0, 8);
        if (toolInput.description !== undefined) updates.description = String(toolInput.description).slice(0, 200);
        if (Object.keys(updates).length === 0) {
          return 'Nothing to update — provide name, emoji, and/or description.';
        }
        await onUpdateApp(project.id, updates);
        const newApp = { ...project, ...updates };
        setActiveProjectBoth(newApp);
        if (project.isPublished) {
          await onPublishApp(project.id);
        }
        return `App info updated: ${newApp.emoji} ${newApp.name}. The app id/link stays "${newApp.appId}".`;
      }

      case 'open_preview':
        onOpenPreview(projectCodeRef.current, projectCssRef.current, project.name, project.emoji);
        return 'Preview window opened/refreshed.';

      case 'publish_app':
        try {
          // Save current code changes first
          await onUpdateApp(project.id, {
            code: projectCodeRef.current,
            css: projectCssRef.current
          });
          // Then publish (this will overwrite existing published version)
          await onPublishApp(project.id);
          setActiveProjectBoth({ ...project, isPublished: true });
          setHasUnpublishedChanges(false);
          const action = project.isPublished ? 'Updated' : 'Published';
          return `${action}! Direct link: inorbit.fm/app/${project.appId}`;
        } catch (error) {
          return `Failed to publish: ${error instanceof Error ? error.message : 'Unknown error'}`;
        }

      case 'unpublish_app':
        if (!project.isPublished) {
          return 'App is not published.';
        }
        try {
          await onUnpublishApp(project.id);
          setActiveProjectBoth({ ...project, isPublished: false });
          setHasUnpublishedChanges(false);
          return 'App removed from store.';
        } catch (error) {
          return `Failed to unpublish: ${error instanceof Error ? error.message : 'Unknown error'}`;
        }

      default:
        return `Unknown tool: ${toolName}`;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // CLAUDE API CALLS
  // ─────────────────────────────────────────────────────────────────────────────

  const callClaudeWithTools = async (
    messages: Array<{role: string; content: any}>,
    useTools: boolean
  ): Promise<any> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Login required to use the terminal');
    }

    const idToken = await user.getIdToken();

    const body: any = {
      model: MODEL,
      max_tokens: MAX_TOKENS,
      system: useTools ? PROJECT_SYSTEM_PROMPT : HOME_SYSTEM_PROMPT,
      messages
    };

    if (useTools) {
      body.tools = TOOLS;
    }

    const response = await fetch(`${API_URL}/orbit/claude`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API error: ${error}`);
    }

    return response.json();
  };

  // Agentic loop for project mode
  const runAgenticLoop = async (userMessage: string) => {
    setIsLoading(true);

    let history = trimHistory([...conversationHistory, { role: 'user', content: userMessage }], 30);
    let toolCallCount = 0;

    try {
      while (toolCallCount < MAX_TOOL_CALLS) {
        const response = await callClaudeWithTools(history, true);

        // Check stop reason
        if (response.stop_reason === 'end_turn') {
          // Final response - extract text
          const textContent = response.content.find((c: any) => c.type === 'text');
          if (textContent) {
            addMessage({ type: 'assistant', content: textContent.text });
          }
          history.push({ role: 'assistant', content: response.content });
          break;
        }

        if (response.stop_reason === 'tool_use') {
          // Process tool calls
          const toolUses = response.content.filter((c: any) => c.type === 'tool_use');
          const textContent = response.content.find((c: any) => c.type === 'text');

          // Show assistant's thinking if any
          if (textContent) {
            addMessage({ type: 'assistant', content: textContent.text });
          }

          // Add assistant message to history
          history.push({ role: 'assistant', content: response.content });

          // Execute each tool call
          const toolResults: any[] = [];
          for (const toolUse of toolUses) {
            toolCallCount++;

            // Show tool call in UI
            addMessage({
              type: 'tool',
              content: `${toolUse.name}`,
              toolName: toolUse.name,
              toolStatus: 'running'
            });

            try {
              const result = await executeToolCall(toolUse.name, toolUse.input);
              updateLastMessage({ toolStatus: 'success', content: `${toolUse.name}: ✓` });
              toolResults.push({
                type: 'tool_result',
                tool_use_id: toolUse.id,
                content: result
              });
            } catch (error) {
              const errMsg = error instanceof Error ? error.message : 'Unknown error';
              updateLastMessage({ toolStatus: 'error', content: `${toolUse.name}: ✗ ${errMsg}` });
              toolResults.push({
                type: 'tool_result',
                tool_use_id: toolUse.id,
                content: `Error: ${errMsg}`,
                is_error: true
              });
            }
          }

          // Add tool results to history
          history.push({ role: 'user', content: toolResults });

          if (toolCallCount >= MAX_TOOL_CALLS) {
            addMessage({
              type: 'system',
              content: '⚠️ Reached maximum tool calls. Please try a simpler request.'
            });
            break;
          }
        } else {
          // Unknown stop reason
          const textContent = response.content.find((c: any) => c.type === 'text');
          if (textContent) {
            addMessage({ type: 'assistant', content: textContent.text });
          }
          break;
        }
      }

      setConversationHistory(history);

    } catch (error) {
      console.error('Agentic loop error:', error);
      addMessage({
        type: 'error',
        content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Home mode: create/refine a new app (conversational — remembers the chat)
  const createNewApp = async (userMessage: string) => {
    setIsLoading(true);

    try {
      const history = trimHistory([...conversationHistory, { role: 'user', content: userMessage }], 12);
      const response = await callClaudeWithTools(history, false);

      const textContent = response.content.find((c: any) => c.type === 'text');
      if (!textContent) {
        addMessage({ type: 'error', content: 'No response from AI' });
        return;
      }

      const text = textContent.text;
      setConversationHistory([...history, { role: 'assistant', content: text }]);

      const parsed = parseGeneratedApp(text);

      if (parsed.code) {
        // Store/replace pending app
        setPendingApp({ code: parsed.code, css: parsed.css, name: parsed.name, emoji: parsed.emoji });

        if (parsed.explanation) {
          addMessage({ type: 'assistant', content: parsed.explanation });
        }

        // Show code result with buttons
        addMessage({
          type: 'code',
          content: 'App generated!',
          code: parsed.code,
          css: parsed.css,
          appName: parsed.name,
          appEmoji: parsed.emoji
        });
      } else {
        // No code, just show response
        addMessage({ type: 'assistant', content: text });
      }

    } catch (error) {
      console.error('Create app error:', error);
      addMessage({
        type: 'error',
        content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // INFO EDITOR (name / emoji / description)
  // ─────────────────────────────────────────────────────────────────────────────

  const openInfoEditor = (app: UserApp) => {
    setInfoEditor({
      app,
      name: app.name,
      emoji: app.emoji,
      description: app.description || ''
    });
  };

  const saveInfoEditor = async () => {
    if (!infoEditor || infoSaving) return;
    const { app } = infoEditor;
    const name = infoEditor.name.trim() || app.name;
    const emoji = infoEditor.emoji.trim() || app.emoji;
    const description = infoEditor.description.trim();

    setInfoSaving(true);
    try {
      await onUpdateApp(app.id, { name, emoji, description });
      if (app.isPublished) {
        // Sync the store listing
        await onPublishApp(app.id);
      }
      if (activeProjectRef.current?.id === app.id) {
        setActiveProjectBoth({ ...activeProjectRef.current, name, emoji, description });
      }
      addMessage({ type: 'system', content: `✓ Updated ${emoji} ${name}` });
      setInfoEditor(null);
    } catch (error) {
      addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
    } finally {
      setInfoSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // DELETE (two-step confirm)
  // ─────────────────────────────────────────────────────────────────────────────

  const handleDeleteApp = async (app: UserApp) => {
    if (deleteConfirmId !== app.id) {
      setDeleteConfirmId(app.id);
      setTimeout(() => setDeleteConfirmId(current => (current === app.id ? null : current)), 4000);
      return;
    }
    setDeleteConfirmId(null);
    try {
      await onDeleteApp(app.id);
      addMessage({ type: 'system', content: `✓ Deleted ${app.emoji} ${app.name}` });
    } catch (error) {
      addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // COMMAND HANDLING (home mode)
  // ─────────────────────────────────────────────────────────────────────────────

  const findAppByQuery = (query: string, apps: Array<{ appId: string; name: string }>) => {
    const q = query.trim().toLowerCase();
    const hyphenated = q.replace(/\s+/g, '-');
    return apps.find(a =>
      a.appId.toLowerCase() === q ||
      a.appId.toLowerCase() === hyphenated ||
      a.name.toLowerCase() === q
    );
  };

  const handleCommand = async (cmd: string) => {
    const parts = cmd.slice(1).split(' ');
    const command = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (command) {
      case 'help':
        addMessage({
          type: 'system',
          content: `Commands:
  /store              - browse published apps
  /install <app-id>   - install an app from the store
  /publish <app-id>   - publish/update your app
  /unpublish <app-id> - remove your app from the store
  /delete <app-id>    - delete your app
  /token              - get a token to build apps from Claude Code (inorbit.fm/dev)
  /login              - login to save apps
  /clear              - clear terminal

Create: just describe an app in plain words.
Edit: click one of your apps above (or its ✎ to change name/icon).`
        });
        break;

      case 'store':
        if (publishedApps.length === 0) {
          addMessage({ type: 'system', content: 'App Store is empty! Be the first to publish.' });
        } else {
          const storeList = publishedApps.map((app) => {
            const installed = installedAppIds.includes(app.appId) ? ' ✓' : '';
            return `  ${app.emoji} ${app.name} (${app.appId}) by @${app.authorHandle}${installed}`;
          }).join('\n');
          addMessage({
            type: 'system',
            content: `App Store (${publishedApps.length})\n\n${storeList}\n\n/install <app-id> to add`
          });
        }
        break;

      case 'install': {
        if (!isLoggedIn) {
          addMessage({ type: 'error', content: 'Login required. Type /login' });
          return;
        }
        const query = args.join(' ');
        if (!query) {
          addMessage({ type: 'error', content: 'Usage: /install <app-id>' });
          return;
        }
        const appToInstall = findAppByQuery(query, publishedApps) as PublishedApp | undefined;
        if (!appToInstall) {
          addMessage({ type: 'error', content: `App not found: ${query}. Use /store to see apps.` });
          return;
        }
        if (installedAppIds.includes(appToInstall.appId)) {
          addMessage({ type: 'system', content: `${appToInstall.emoji} ${appToInstall.name} is already installed.` });
          return;
        }
        try {
          await onInstallApp(appToInstall.appId);
          addMessage({ type: 'system', content: `✓ Installed ${appToInstall.emoji} ${appToInstall.name}` });
        } catch (error) {
          addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
        }
        break;
      }

      case 'login':
        onOpenLoginSignup();
        break;

      case 'token': {
        if (!isLoggedIn) {
          addMessage({ type: 'error', content: 'Login required. Type /login' });
          return;
        }
        const user = auth.currentUser;
        if (!user) {
          addMessage({ type: 'error', content: 'Login required. Type /login' });
          return;
        }
        const sub = (args[0] || 'new').toLowerCase();
        setIsLoading(true);
        try {
          const idToken = await user.getIdToken();
          if (sub === 'revoke') {
            const res = await fetch(`${API_URL}/orbit/token/revoke`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` }
            });
            if (!res.ok) throw new Error(await res.text());
            const { revoked } = await res.json();
            addMessage({ type: 'system', content: `✓ Revoked ${revoked} token(s). Reconnected tools will stop working.` });
          } else {
            const res = await fetch(`${API_URL}/orbit/token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({ label: 'cli' })
            });
            if (!res.ok) throw new Error(await res.text());
            const { token } = await res.json();
            addMessage({
              type: 'system',
              content: `Your API token (shown once — copy it now):

${token}

Build & publish apps from Claude Code:
claude mcp add --transport http inorbit ${API_URL}/orbit/mcp --header "Authorization: Bearer ${token}"

Codex (~/.codex/config.toml):
[mcp_servers.inorbit]
url = "${API_URL}/orbit/mcp"
http_headers = { "Authorization" = "Bearer ${token}" }

Full guide: inorbit.fm/dev  •  /token revoke to revoke all tokens`
            });
          }
        } catch (error) {
          addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
        }
        setIsLoading(false);
        break;
      }

      case 'clear':
        setMessages([]);
        setConversationHistory([]);
        setPendingApp(null);
        addMessage({ type: 'system', content: 'Cleared.' });
        break;

      case 'publish': {
        if (!isLoggedIn) {
          addMessage({ type: 'error', content: 'Login required. Type /login' });
          return;
        }
        const appIdArg = args.join(' ');
        if (!appIdArg) {
          addMessage({ type: 'error', content: 'Usage: /publish <app-id>' });
          return;
        }
        const appToPublish = findAppByQuery(appIdArg, userApps) as UserApp | undefined;
        if (!appToPublish) {
          addMessage({ type: 'error', content: `App not found: ${appIdArg}` });
          return;
        }
        try {
          await onPublishApp(appToPublish.id);
          const action = appToPublish.isPublished ? 'Updated' : 'Published';
          addMessage({
            type: 'system',
            content: `✓ ${action} ${appToPublish.emoji} ${appToPublish.name}\n  Link: inorbit.fm/app/${appToPublish.appId}`
          });
        } catch (error) {
          addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
        }
        break;
      }

      case 'unpublish': {
        if (!isLoggedIn) {
          addMessage({ type: 'error', content: 'Login required. Type /login' });
          return;
        }
        const unpubAppId = args.join(' ');
        if (!unpubAppId) {
          addMessage({ type: 'error', content: 'Usage: /unpublish <app-id>' });
          return;
        }
        const appToUnpub = findAppByQuery(unpubAppId, userApps) as UserApp | undefined;
        if (!appToUnpub) {
          addMessage({ type: 'error', content: `App not found: ${unpubAppId}` });
          return;
        }
        if (!appToUnpub.isPublished) {
          addMessage({ type: 'error', content: `${appToUnpub.emoji} ${appToUnpub.name} is not published.` });
          return;
        }
        try {
          await onUnpublishApp(appToUnpub.id);
          addMessage({ type: 'system', content: `✓ Unpublished ${appToUnpub.emoji} ${appToUnpub.name}` });
        } catch (error) {
          addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
        }
        break;
      }

      case 'delete': {
        if (!isLoggedIn) {
          addMessage({ type: 'error', content: 'Login required. Type /login' });
          return;
        }
        const deleteArg = args.join(' ');
        if (!deleteArg) {
          addMessage({ type: 'error', content: 'Usage: /delete <app-id> (or use the ✕ button in your app list)' });
          return;
        }
        const appToDelete = findAppByQuery(deleteArg, userApps) as UserApp | undefined;
        if (!appToDelete) {
          addMessage({ type: 'error', content: `App not found: ${deleteArg}` });
          return;
        }
        await handleDeleteApp(appToDelete);
        if (deleteConfirmId !== appToDelete.id) {
          addMessage({ type: 'system', content: `Delete ${appToDelete.emoji} ${appToDelete.name}? Run the command again (or click ✕ twice) to confirm.` });
        }
        break;
      }

      default:
        addMessage({ type: 'error', content: `Unknown: /${command}. Try /help` });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FORM SUBMISSION
  // ─────────────────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userInput = input.trim();
    setInput('');

    // Commands (home mode only)
    if (userInput.startsWith('/') && mode === 'home') {
      addMessage({ type: 'user', content: userInput });
      await handleCommand(userInput);
      return;
    }

    // Project mode direct commands (don't need LLM)
    if (userInput.startsWith('/') && mode === 'project' && activeProject) {
      const cmd = userInput.slice(1).toLowerCase().trim().split(' ')[0];
      addMessage({ type: 'user', content: userInput });

      if (cmd === 'publish') {
        setIsLoading(true);
        try {
          // Save current changes first
          await onUpdateApp(activeProject.id, {
            code: projectCodeRef.current,
            css: projectCssRef.current
          });
          await onPublishApp(activeProject.id);
          const action = activeProject.isPublished ? 'Updated' : 'Published';
          setActiveProjectBoth({ ...activeProject, isPublished: true });
          setHasUnpublishedChanges(false);
          addMessage({
            type: 'system',
            content: `✓ ${action} ${activeProject.emoji} ${activeProject.name}\n  Link: inorbit.fm/app/${activeProject.appId}`
          });
        } catch (error) {
          addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
        }
        setIsLoading(false);
        return;
      }

      if (cmd === 'unpublish') {
        if (!activeProject.isPublished) {
          addMessage({ type: 'error', content: 'App is not published.' });
          return;
        }
        setIsLoading(true);
        try {
          await onUnpublishApp(activeProject.id);
          setActiveProjectBoth({ ...activeProject, isPublished: false });
          setHasUnpublishedChanges(false);
          addMessage({ type: 'system', content: `✓ Unpublished ${activeProject.emoji} ${activeProject.name}` });
        } catch (error) {
          addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
        }
        setIsLoading(false);
        return;
      }

      if (cmd === 'exit') {
        exitProject();
        return;
      }

      if (cmd === 'preview') {
        onOpenPreview(projectCodeRef.current, projectCssRef.current, activeProject.name, activeProject.emoji);
        addMessage({ type: 'system', content: '✓ Preview opened' });
        return;
      }

      if (cmd === 'code') {
        openManualCodeEditor();
        return;
      }

      if (cmd === 'info' || cmd === 'rename' || cmd === 'icon') {
        openInfoEditor(activeProject);
        return;
      }

      if (cmd === 'help') {
        addMessage({
          type: 'system',
          content: `Project commands:
  /publish   - save & publish app
  /unpublish - remove from store
  /preview   - open preview window
  /code      - edit code by hand
  /info      - rename / change icon
  /exit      - back to home

Or just describe the change you want — edits save automatically.`
        });
        return;
      }

      addMessage({ type: 'error', content: `Unknown: /${cmd}. Try /help — or describe the change in plain words (no slash).` });
      return;
    }

    // Add user message
    addMessage({ type: 'user', content: userInput });

    if (mode === 'project') {
      // Project mode: run agentic loop
      await runAgenticLoop(userInput);
    } else {
      // Home mode: create new app
      if (!isLoggedIn) {
        addMessage({ type: 'error', content: 'Login required to create apps. Type /login' });
        return;
      }
      await createNewApp(userInput);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ACTION HANDLERS (for code result buttons, manual editor)
  // ─────────────────────────────────────────────────────────────────────────────

  const openManualCodeEditor = () => {
    const app = activeProjectRef.current;
    if (!app) return;
    onOpenCodeEditor(
      projectCodeRef.current,
      projectCssRef.current,
      app.name,
      (newCode, newCss) => {
        applyProjectCode(newCode, newCss);
      }
    );
  };

  const handlePreview = () => {
    if (pendingApp) {
      onOpenPreview(pendingApp.code, pendingApp.css, pendingApp.name, pendingApp.emoji);
    }
  };

  const handleSave = async () => {
    if (!pendingApp || !isLoggedIn) return;

    try {
      const firebaseId = await onSaveApp({
        name: pendingApp.name,
        emoji: pendingApp.emoji,
        code: pendingApp.code,
        css: pendingApp.css,
        description: ''
      });

      addMessage({ type: 'system', content: `✓ Saved ${pendingApp.emoji} ${pendingApp.name}` });

      // Wait a moment for userApps state to update, then enter project mode
      setTimeout(() => {
        const savedApp = userApps.find(a => a.id === firebaseId);
        if (savedApp) {
          setPendingApp(null);
          enterProject(savedApp);
        } else {
          // Fallback: construct the app object if state hasn't updated yet
          const appSlug = pendingApp.name
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '') || 'app';

          const newApp: UserApp = {
            id: firebaseId,
            appId: appSlug,
            name: pendingApp.name,
            emoji: pendingApp.emoji,
            code: pendingApp.code,
            css: pendingApp.css,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            isPublished: false,
            description: ''
          };
          setPendingApp(null);
          enterProject(newApp);
        }
      }, 300);
    } catch (error) {
      addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
    }
  };

  const handleDiscard = () => {
    setPendingApp(null);
    addMessage({ type: 'system', content: 'Discarded.' });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  // Helper to render text with clickable links
  const renderWithLinks = (text: string) => {
    // Match inorbit.fm/app/... links
    const linkRegex = /(inorbit\.fm\/app\/[\w-]+)/g;
    const parts = text.split(linkRegex);

    return parts.map((part, i) => {
      if (linkRegex.test(part)) {
        // Reset regex lastIndex
        linkRegex.lastIndex = 0;
        const appPath = part.replace('inorbit.fm', '');
        return (
          <a
            key={i}
            href={appPath}
            target="_blank"
            rel="noopener noreferrer"
            className="terminal-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const renderMessage = (message: Message) => {
    switch (message.type) {
      case 'system':
        return <div className="terminal-message system">{renderWithLinks(message.content)}</div>;

      case 'user':
        return (
          <div className="terminal-message user">
            <span className="prompt">&gt;</span> {message.content}
          </div>
        );

      case 'assistant':
        return <div className="terminal-message assistant">{message.content}</div>;

      case 'error':
        return (
          <div className="terminal-message error">
            <span className="error-icon">✗</span> {message.content}
          </div>
        );

      case 'tool':
        return (
          <div className={`tool-call ${message.toolStatus || ''}`}>
            <span className="tool-call-icon">
              {message.toolStatus === 'running' ? '◐' : message.toolStatus === 'success' ? '✓' : '✗'}
            </span>
            <span className="tool-call-name">{message.content}</span>
          </div>
        );

      case 'code':
        return (
          <div className="terminal-message code-result">
            <div className="code-header">
              <span>{message.appEmoji} {message.appName}</span>
            </div>
            <div className="code-preview">
              <pre>{message.code?.slice(0, 200)}...</pre>
            </div>
            <div className="code-actions">
              <button onClick={handlePreview}>Preview</button>
              <button onClick={handleSave} className="save-btn">Save & edit</button>
              <button onClick={handleDiscard} className="discard-btn">Discard</button>
            </div>
            <div className="code-hint">Not quite right? Keep chatting to refine it before saving.</div>
          </div>
        );

      default:
        return <div className="terminal-message">{message.content}</div>;
    }
  };

  const saveStatusLabel =
    saveStatus === 'saving' ? 'Saving…'
    : saveStatus === 'saved' ? 'Saved'
    : saveStatus === 'error' ? 'Save failed'
    : '';

  return (
    <div className={`terminal ${mode}-mode`}>
      {/* Header */}
      <div className="terminal-header">
        <div className="terminal-header-left">
          {mode === 'home' ? (
            <span className="terminal-header-title">Orbit Terminal</span>
          ) : (
            <button
              className="terminal-header-title project-title-btn"
              title="Rename / change icon"
              onClick={() => activeProject && openInfoEditor(activeProject)}
            >
              {activeProject?.emoji} {activeProject?.name} <span className="title-edit-hint">✎</span>
            </button>
          )}
          {mode === 'project' && activeProject && (
            <span className="terminal-header-subtitle">
              {activeProject.appId}
              {activeProject.isPublished ? ' • published' : ' • draft'}
              {saveStatusLabel && <span className={`save-status ${saveStatus}`}> • {saveStatusLabel}</span>}
            </span>
          )}
        </div>
        <div className="terminal-header-right">
          {mode === 'project' && activeProject && (
            <button className="header-code-btn" onClick={openManualCodeEditor} title="Edit code by hand">
              Code
            </button>
          )}
          {mode === 'project' && activeProject && (
            (!activeProject.isPublished || hasUnpublishedChanges) && (
              <button
                className="header-publish-btn"
                onClick={async () => {
                  try {
                    await onUpdateApp(activeProject.id, { code: projectCodeRef.current, css: projectCssRef.current });
                    await onPublishApp(activeProject.id);
                    setActiveProjectBoth({
                      ...activeProject,
                      isPublished: true,
                      code: projectCodeRef.current,
                      css: projectCssRef.current
                    });
                    setHasUnpublishedChanges(false);
                    addMessage({
                      type: 'system',
                      content: `✓ ${activeProject.isPublished ? 'Updated' : 'Published'} ${activeProject.emoji} ${activeProject.name}\n  Link: inorbit.fm/app/${activeProject.appId}`
                    });
                  } catch (error) {
                    addMessage({ type: 'error', content: `Failed: ${error instanceof Error ? error.message : 'Unknown'}` });
                  }
                }}
              >
                {activeProject.isPublished ? 'Update' : 'Publish'}
              </button>
            )
          )}
          {mode === 'project' && activeProject?.isPublished && (
            <a
              href={`/app/${activeProject.appId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="header-link-btn"
            >
              Link ↗
            </a>
          )}
          {mode === 'project' && (
            <button className="exit-project-btn" onClick={exitProject}>
              Exit
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="terminal-output">
        {/* App List (home mode only - scrolls with content) */}
        {mode === 'home' && isLoggedIn && (
          <div className="app-list-section">
            <div className="app-list-header">Your Apps</div>
            <div className="app-list">
              {userApps.length === 0 ? (
                <div className="app-list-empty">No apps yet. Describe one below and I'll build it!</div>
              ) : (
                userApps.map(app => (
                  <div key={app.id} className="app-list-item">
                    <button
                      className="app-row-main"
                      onClick={() => enterProject(app)}
                      title="Open in editor"
                    >
                      <span className="app-list-item-emoji">{app.emoji}</span>
                      <span className="app-list-item-name">{app.name}</span>
                      <span className="app-list-item-id">({app.appId})</span>
                      {app.format === 'html-v2' && <span className="app-list-item-ext">ext</span>}
                      {app.isPublished && <span className="app-list-item-published">published</span>}
                    </button>
                    <div className="app-row-actions">
                      <button
                        className="app-action-btn"
                        title="Rename / change icon"
                        onClick={() => openInfoEditor(app)}
                      >✎</button>
                      {app.isPublished && (
                        <a
                          className="app-action-btn"
                          title="Open public link"
                          href={`/app/${app.appId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >↗</a>
                      )}
                      <button
                        className={`app-action-btn delete ${deleteConfirmId === app.id ? 'confirm' : ''}`}
                        title={deleteConfirmId === app.id ? 'Click again to delete forever' : 'Delete app'}
                        onClick={() => handleDeleteApp(app)}
                      >{deleteConfirmId === app.id ? 'sure?' : '✕'}</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Commands hint (home mode only) */}
        {mode === 'home' && (
          <div className="commands-section">
            <div className="commands-list">
              <span className="command-hint">describe an app to build it</span>
              <span className="command-hint"><code>/store</code> browse</span>
              <span className="command-hint"><code>/help</code> commands</span>
            </div>
          </div>
        )}

        {messages.map(message => (
          <div key={message.id}>
            {renderMessage(message)}
          </div>
        ))}
        {isLoading && (
          <div className="terminal-message loading">
            <span className="loading-spinner">◐</span> {mode === 'project' ? 'Working…' : 'Building… (this can take up to a minute)'}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form className="terminal-input-form" onSubmit={handleSubmit}>
        <span className="input-prompt">&gt;</span>
        <input
          ref={inputRef}
          type="text"
          className="terminal-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            isLoading
              ? 'Working…'
              : mode === 'project'
                ? 'Describe a change…'
                : 'Describe an app to create…'
          }
          disabled={isLoading}
          autoFocus
        />
      </form>

      {/* Info editor overlay (name / emoji / description) */}
      {infoEditor && (
        <div className="info-editor-overlay" onClick={() => !infoSaving && setInfoEditor(null)}>
          <div className="info-editor" onClick={(e) => e.stopPropagation()}>
            <div className="info-editor-title">Edit app info</div>

            <label className="info-editor-label">Icon</label>
            <div className="emoji-picker">
              {EMOJI_CHOICES.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`emoji-choice ${infoEditor.emoji === emoji ? 'selected' : ''}`}
                  onClick={() => setInfoEditor({ ...infoEditor, emoji })}
                >{emoji}</button>
              ))}
            </div>
            <input
              className="info-editor-input emoji-input"
              type="text"
              value={infoEditor.emoji}
              maxLength={8}
              onChange={(e) => setInfoEditor({ ...infoEditor, emoji: e.target.value })}
              placeholder="…or type any emoji"
            />

            <label className="info-editor-label">Name</label>
            <input
              className="info-editor-input"
              type="text"
              value={infoEditor.name}
              maxLength={40}
              onChange={(e) => setInfoEditor({ ...infoEditor, name: e.target.value })}
              placeholder="App name"
            />

            <label className="info-editor-label">Description</label>
            <textarea
              className="info-editor-input info-editor-textarea"
              value={infoEditor.description}
              maxLength={200}
              onChange={(e) => setInfoEditor({ ...infoEditor, description: e.target.value })}
              placeholder="What does this app do? (shown in the store)"
            />

            <div className="info-editor-note">
              Link stays inorbit.fm/app/{infoEditor.app.appId}
              {infoEditor.app.isPublished ? ' — store listing updates too.' : ''}
            </div>

            <div className="info-editor-actions">
              <button
                type="button"
                className="info-editor-cancel"
                disabled={infoSaving}
                onClick={() => setInfoEditor(null)}
              >Cancel</button>
              <button
                type="button"
                className="info-editor-save"
                disabled={infoSaving}
                onClick={saveInfoEditor}
              >{infoSaving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Terminal;
